import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {IBulkUploadRow, IBulkUploadRowPatchRequest, BulkUploadChildDraft} from "../../model/IBulkUpload";
import {format} from "date-fns";
import CategoryPicker from "../ui/transaction/CategoryPicker";
import {categoryApi} from "../../api/CategoriesService";
import {TransactionType} from "../../model/TransactionType";
import {classNames} from "../../utils/common";
import CommentField from "../ui/transaction/CommentField";
import AmountField from "../ui/transaction/AmountField";
import TagsPicker from "../ui/transaction/TagsPicker";
import AccountPicker from "../ui/transaction/AccountPicker";
import {Tag} from "../../model/ICategory";
import useActiveAccountsSelector from "../../hooks/useActiveAccountsSelector";
import {IAccount} from "../../model/IAccount";
import {Menu, Transition} from "@headlessui/react";
import {EllipsisVerticalIcon} from "@heroicons/react/24/outline";

const CATEGORY_TYPE_LABELS: Record<string, string> = {
    expense: 'Expense',
    income: 'Income',
    transfer: 'Transfer',
    exchange: 'Exchange',
};

const normalizeCategoryType = (value: string | undefined | null): TransactionType => {
    const normalized = (value || '').toLowerCase();
    if (normalized === 'income') {
        return 'income' as TransactionType;
    }
    if (normalized === 'transfer') {
        return 'transfer' as TransactionType;
    }
    if (normalized === 'exchange') {
        return 'exchange' as TransactionType;
    }
    return 'expense' as TransactionType;
};

const formatAbsAmount = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const numeric = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
    if (Number.isNaN(numeric)) {
        return String(value).replace(/^-/, '');
    }
    return String(Math.abs(numeric));
};

const parseAmountInput = (value: string): number | null => {
    const cleaned = value.replace(',', '.').trim();
    if (!cleaned) {
        return null;
    }
    const numeric = Number(cleaned);
    return Number.isNaN(numeric) ? null : numeric;
};

export type BulkUploadRowMode = 'normal' | 'split-parent' | 'child' | 'unite-parent' | 'unite-child';

const BulkUploadRowItem: React.FunctionComponent<{
    row: IBulkUploadRow,
    handleCategoryChange(event: any): void,
    checked: boolean,
    accountCurrencySymbol: string | undefined,
    sessionAccountId?: number,
    rowMode?: BulkUploadRowMode,
    onSplit?: () => void,
    onSplitParentSave?: () => void,
    onSplitParentExclude?: () => void,
    onCancelUnite?: () => void,
    splitExcludeLabel?: string,
    allChildrenHaveCategory?: boolean,
    childDraft?: BulkUploadChildDraft,
    onChildDraftChange?: (patch: Partial<BulkUploadChildDraft>) => void,
    onDeleteChild?: () => void,
    canDeleteChild?: boolean,
}> = ({
    row,
    handleCategoryChange,
    checked,
    accountCurrencySymbol,
    sessionAccountId,
    rowMode = 'normal',
    onSplit,
    onSplitParentSave,
    onSplitParentExclude,
    onCancelUnite,
    splitExcludeLabel,
    allChildrenHaveCategory = false,
    childDraft,
    onChildDraftChange,
    onDeleteChild,
    canDeleteChild = false,
}) => {
    const isChild = rowMode === 'child';
    const isSplitParent = rowMode === 'split-parent';
    const isUniteParent = rowMode === 'unite-parent';
    const isUniteChild = rowMode === 'unite-child';
    const isNestedChild = isChild || isUniteChild;
    const savedCategoryType = useMemo(
        () => normalizeCategoryType(row.category_type),
        [row.category_type]
    );
    const [category, setCategory] = useState(null as any);
    const [accountTo, setAccountTo] = useState<IAccount | null>(null);
    const [categoryType, setCategoryType] = useState<TransactionType>(
        isChild ? TransactionType.Expense : savedCategoryType
    );
    const [tags, setTags] = useState([] as Tag[]);
    const [comment, setComment] = useState(row.comment ? row.comment : "");
    const [transferAmount, setTransferAmount] = useState(
        formatAbsAmount(row.transfer_amount ?? row.amount)
    );
    const [totalAmount, setTotalAmount] = useState(formatAbsAmount(row.total));
    const [status, setStatus] = useState(row.status);
    const [modifiedCategory, setModifiedCategory] = useState(false);
    const [modifiedAccountTo, setModifiedAccountTo] = useState(false);
    const [modifiedCategoryType, setModifiedCategoryType] = useState(false);
    const [modifiedComment, setModifiedComment] = useState(false);
    const [modifiedTransferAmount, setModifiedTransferAmount] = useState(false);
    const [modifiedTotalAmount, setModifiedTotalAmount] = useState(false);
    const [modifiedTags, setModifiedTags] = useState(false);
    const {data: expenseCategories} = categoryApi.useFetchAllCategoriesQuery(TransactionType.Expense);
    const {data: incomeCategories} = categoryApi.useFetchAllCategoriesQuery(TransactionType.Income);
    const {data: tagsList} = categoryApi.useFetchAllTagsQuery();
    const {activeAccounts} = useActiveAccountsSelector();
    const [patchBulkUploadRow, {isLoading: isPatching}] = categoryApi.usePatchBulkUploadRowMutation();

    const sessionAccount = useMemo(
        () => activeAccounts.find(account => account.id === sessionAccountId),
        [activeAccounts, sessionAccountId]
    );

    const rowMatchesSessionCurrency = !!sessionAccount && row.currency === sessionAccount.currency.id;
    const rowDiffersFromSessionCurrency = !!sessionAccount && row.currency !== sessionAccount.currency.id;
    const hasTransferAccounts = useMemo(() => {
        if (!sessionAccount) {
            return false;
        }
        return activeAccounts.some(account =>
            account.id !== sessionAccount.id && account.currency.id === sessionAccount.currency.id
        );
    }, [activeAccounts, sessionAccount]);
    const hasExchangeAccounts = useMemo(() => {
        if (!sessionAccount) {
            return false;
        }
        return activeAccounts.some(account =>
            account.id !== sessionAccount.id && account.currency.id === row.currency
        );
    }, [activeAccounts, sessionAccount, row.currency]);

    const isTransferOrExchange =
        categoryType === TransactionType.Transfer || categoryType === TransactionType.Exchange;
    const isPositiveFlowBranch =
        categoryType === TransactionType.Income
        || (isTransferOrExchange && row.positive_flow);

    const availableCategoryTypeOptions = useMemo(() => {
        if (isChild) {
            return [{value: TransactionType.Expense, label: CATEGORY_TYPE_LABELS.expense}];
        }
        if (isUniteChild || isUniteParent) {
            return [{value: categoryType, label: CATEGORY_TYPE_LABELS[categoryType] || CATEGORY_TYPE_LABELS.expense}];
        }
        const options: {value: TransactionType, label: string}[] = [];
        const addOption = (type: TransactionType) => {
            if (!options.some(option => option.value === type)) {
                options.push({value: type, label: CATEGORY_TYPE_LABELS[type]});
            }
        };

        addOption(categoryType);

        if (isPositiveFlowBranch) {
            addOption(TransactionType.Income);
            if (rowMatchesSessionCurrency && hasTransferAccounts) {
                addOption(TransactionType.Transfer);
            }
            if (rowDiffersFromSessionCurrency && hasExchangeAccounts) {
                addOption(TransactionType.Exchange);
            }
        } else {
            addOption(TransactionType.Expense);
            if (rowMatchesSessionCurrency && hasTransferAccounts) {
                addOption(TransactionType.Transfer);
            }
            if (rowDiffersFromSessionCurrency && hasExchangeAccounts) {
                addOption(TransactionType.Exchange);
            }
        }

        return options;
    }, [
        isChild,
        isUniteChild,
        isUniteParent,
        categoryType,
        isPositiveFlowBranch,
        rowMatchesSessionCurrency,
        rowDiffersFromSessionCurrency,
        hasTransferAccounts,
        hasExchangeAccounts,
    ]);

    const accountsTo = useMemo(() => {
        if (!sessionAccount) {
            return [];
        }
        return activeAccounts.filter(account => {
            if (account.id === sessionAccount.id) {
                return false;
            }
            if (categoryType === TransactionType.Transfer) {
                return account.currency.id === sessionAccount.currency.id;
            }
            if (categoryType === TransactionType.Exchange) {
                return account.currency.id === row.currency;
            }
            return false;
        });
    }, [activeAccounts, sessionAccount, categoryType, row.currency]);

    useEffect(() => {
        if (accountTo && !accountsTo.some(account => account.id === accountTo.id)) {
            setAccountTo(null);
            setModifiedAccountTo(row.account_to != null);
        }
    }, [accountsTo, accountTo, row.account_to]);

    const isUpdateStatus = status === 'UPDATE' || status === 'UPDATED';
    const isLockedStatus = status === 'CREATED' || status === 'UPDATED' || status === 'DUPLICATE' || status === 'SPLIT' || status === 'SPLITED' || status === 'UNITED';
    const canEditAmounts = status === 'UPDATE';

    const isTypeSelectLocked =
        isChild
        || isUniteChild
        || isUniteParent
        || status === 'CREATED'
        || status === 'UPDATE'
        || status === 'UPDATED'
        || status === 'SPLIT'
        || status === 'SPLITED'
        || status === 'UNITED'
        || availableCategoryTypeOptions.length <= 1;

    const showCategoryPicker =
        !isUpdateStatus
        && !isSplitParent
        && !isUniteChild
        && status !== 'UNITED'
        && (categoryType === TransactionType.Expense || categoryType === TransactionType.Income);
    const showAccountPicker =
        !isUpdateStatus
        && !isChild
        && !isUniteChild
        && !isUniteParent
        && !isSplitParent
        && (categoryType === TransactionType.Transfer || categoryType === TransactionType.Exchange);
    const showLockedCategoryOrAccount = (isUpdateStatus || isSplitParent || isUniteChild || status === 'UNITED') && (
        categoryType === TransactionType.Expense
        || categoryType === TransactionType.Income
        || categoryType === TransactionType.Transfer
        || categoryType === TransactionType.Exchange
    );
    const showExchangeAmountField =
        !isChild
        && categoryType === TransactionType.Exchange
        && row.positive_flow
        && (canEditAmounts || (!isUpdateStatus && !!accountTo));
    const showUpdateTotalAmountField =
        !isChild
        && canEditAmounts
        && !(categoryType === TransactionType.Exchange && row.positive_flow);
    const showComment =
        !isUpdateStatus
        && !isSplitParent
        && !isUniteChild
        && status !== 'UNITED'
        && (categoryType === TransactionType.Expense || categoryType === TransactionType.Income)
        && !showExchangeAmountField;
    const showTags =
        !isUpdateStatus
        && !isSplitParent
        && !isUniteChild
        && status !== 'UNITED'
        && (categoryType === TransactionType.Expense || categoryType === TransactionType.Income);

    const categoriesForType = useMemo(() => {
        if (categoryType === TransactionType.Expense) {
            return expenseCategories || [];
        }
        if (categoryType === TransactionType.Income) {
            return incomeCategories || [];
        }
        return [];
    }, [categoryType, expenseCategories, incomeCategories]);

    useEffect(() => {
        if (isChild) {
            setCategoryType(TransactionType.Expense);
            setModifiedCategoryType(false);
            return;
        }
        setCategoryType(savedCategoryType);
        setModifiedCategoryType(false);
    }, [savedCategoryType, isChild]);

    useEffect(() => {
        if (isChild) {
            const categoryId = childDraft?.categoryId ?? row.category;
            if (!categoryId) {
                setCategory(null);
                return;
            }
            const matched = (expenseCategories || []).find(cat => cat.id === categoryId);
            setCategory(matched || null);
            return;
        }
        if (!row.category) {
            setCategory(null);
            setModifiedCategory(false);
            return;
        }
        const list = savedCategoryType === TransactionType.Expense ? expenseCategories : incomeCategories;
        if (list && list.length !== 0) {
            handleSetCategory(list.find(cat => cat.id === row.category));
        }
    }, [expenseCategories, incomeCategories, row.category, savedCategoryType, isChild, childDraft?.categoryId]);

    useEffect(() => {
        if (!row.account_to) {
            setAccountTo(null);
            setModifiedAccountTo(false);
            return;
        }
        const matched = activeAccounts.find(account => account.id === row.account_to) || null;
        setAccountTo(matched);
        setModifiedAccountTo(false);
    }, [activeAccounts, row.account_to]);

    useEffect(() => {
        if (isChild) {
            setComment(childDraft?.comment ?? (row.comment ? row.comment : ""));
            return;
        }
        handleSetComment(row.comment ? row.comment : "");
    }, [row.comment, isChild, childDraft?.comment]);

    useEffect(() => {
        const nextAmount = formatAbsAmount(row.transfer_amount ?? row.amount);
        setTransferAmount(nextAmount);
        setModifiedTransferAmount(false);
    }, [row.transfer_amount, row.amount]);

    useEffect(() => {
        setTotalAmount(formatAbsAmount(row.total));
        setModifiedTotalAmount(false);
    }, [row.total]);

    useEffect(() => {
        setStatus(row.status);
    }, [row.status]);

    useEffect(() => {
        if (!tagsList) {
            return;
        }
        if (isChild) {
            const ids = childDraft?.tagIds ?? (row.tags || []).map(tag => tag.id);
            setTags(tagsList.filter(tag => ids.includes(tag.id)));
            return;
        }
        setTags(tagsList.filter(tag => row.tags.some(trTag => trTag.id === tag.id)));
    }, [tagsList, row.tags, isChild, childDraft?.tagIds]);

    const handleSetComment = (newComment: string) => {
        if (isChild) {
            onChildDraftChange?.({comment: newComment});
            setComment(newComment);
            return;
        }
        setModifiedComment(!(row.comment === null && newComment.length === 0 || newComment === row.comment));
        setComment(newComment);
    }

    const handleSetTransferAmount = (newAmount: string) => {
        setTransferAmount(newAmount);
        const saved = formatAbsAmount(row.transfer_amount ?? row.amount);
        setModifiedTransferAmount(formatAbsAmount(newAmount) !== saved);
    }

    const handleSetTotalAmount = (newAmount: string) => {
        setTotalAmount(newAmount);
        setModifiedTotalAmount(formatAbsAmount(newAmount) !== formatAbsAmount(row.total));
    }

    const handleSetTags = (newTags: Tag[]) => {
        if (isChild) {
            onChildDraftChange?.({tagIds: newTags.map(tag => tag.id)});
            setTags(newTags);
            return;
        }
        setModifiedTags(!(
            row.tags === null && newTags === null
            || row.tags.length === 0 && newTags.length === 0
            || newTags.every(tag => row.tags.map(tag => tag.id).includes(tag.id))
            && row.tags.every(tag => newTags.map(tag => tag.id).includes(tag.id))));
        setTags(newTags);
    }

    const handleSetCategory = (newCategory: any) => {
        if (isChild) {
            onChildDraftChange?.({categoryId: newCategory ? newCategory.id : null});
            setCategory(newCategory || null);
            return;
        }
        if (!newCategory) {
            setCategory(null);
            setModifiedCategory(row.category != null);
            return;
        }
        setModifiedCategory(newCategory.id !== row.category);
        setCategory(newCategory);
    }

    const handleSetAccountTo = (newAccount: IAccount | null) => {
        setAccountTo(newAccount);
        setModifiedAccountTo((newAccount?.id ?? null) !== (row.account_to ?? null));
    }

    const handleCategoryTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (isChild || isSplitParent || isUniteParent || isUniteChild) {
            return;
        }
        const newType = normalizeCategoryType(event.target.value);
        setCategoryType(newType);
        setModifiedCategoryType(newType !== savedCategoryType);

        if (newType === TransactionType.Expense || newType === TransactionType.Income) {
            setAccountTo(null);
            setModifiedAccountTo(row.account_to != null);
            if (row.transfer_amount != null) {
                setModifiedTransferAmount(true);
            }
        } else {
            setCategory(null);
            setModifiedCategory(row.category != null);
        }
    }

    const handleToggleStatus = (e: any) => {
        if (isUniteParent) {
            if (status === 'UNITE') {
                onCancelUnite?.();
            }
            return;
        }
        if (isSplitParent) {
            if (status === 'SPLIT') {
                onSplitParentExclude?.();
            }
            return;
        }
        if (isChild || isUniteChild || isLockedStatus || status === 'UPDATE') {
            return;
        }
        const newStatus = status === 'UPLOADED' ? 'EXCLUDED' : 'UPLOADED';
        patchBulkUploadRow({id: row.id, status: newStatus});
        setStatus(newStatus);
    }

    const modified = useMemo(() => {
        return modifiedCategory
            || modifiedAccountTo
            || modifiedCategoryType
            || modifiedComment
            || modifiedTransferAmount
            || modifiedTotalAmount
            || modifiedTags;
    }, [
        modifiedCategory,
        modifiedAccountTo,
        modifiedCategoryType,
        modifiedComment,
        modifiedTransferAmount,
        modifiedTotalAmount,
        modifiedTags,
    ]);

    const isRowConfigured = useMemo(() => {
        if (isSplitParent) {
            return allChildrenHaveCategory;
        }
        if (isUniteParent) {
            return !!category;
        }
        if (isUniteChild) {
            return !!row.category;
        }
        if (isChild) {
            return !!(childDraft?.categoryId || category);
        }
        if (status === 'UPDATE' || status === 'UPDATED') {
            if (showExchangeAmountField) {
                return parseAmountInput(transferAmount) != null;
            }
            if (showUpdateTotalAmountField) {
                return parseAmountInput(totalAmount) != null;
            }
            return !!row.transaction_id;
        }
        if (showCategoryPicker) {
            return !!category;
        }
        if (showAccountPicker) {
            if (!accountTo) {
                return false;
            }
            if (categoryType === TransactionType.Exchange && row.positive_flow) {
                return parseAmountInput(transferAmount) != null;
            }
            return true;
        }
        return false;
    }, [
        isSplitParent,
        allChildrenHaveCategory,
        isUniteParent,
        isUniteChild,
        isChild,
        childDraft?.categoryId,
        status,
        showCategoryPicker,
        showAccountPicker,
        showExchangeAmountField,
        showUpdateTotalAmountField,
        category,
        accountTo,
        transferAmount,
        totalAmount,
        categoryType,
        row.positive_flow,
        row.transaction_id,
    ]);

    const handleSave = () => {
        if (isSplitParent) {
            if (status === 'SPLIT') {
                onSplitParentSave?.();
            }
            return;
        }
        if (isChild || isUniteChild) {
            return;
        }
        if (!modified) {
            alert("Nothing to Save");
            return;
        }
        if (canEditAmounts) {
            const payload: IBulkUploadRowPatchRequest = {id: row.id};
            if (showExchangeAmountField) {
                payload.transfer_amount = parseAmountInput(transferAmount);
            }
            if (showUpdateTotalAmountField || showExchangeAmountField) {
                payload.total = parseAmountInput(totalAmount);
            }
            if (categoryType === TransactionType.Exchange && !row.positive_flow && modifiedTransferAmount) {
                payload.amount = parseAmountInput(transferAmount);
            }
            patchBulkUploadRow(payload);
            setModifiedTransferAmount(false);
            setModifiedTotalAmount(false);
            return;
        }
        const payload: IBulkUploadRowPatchRequest = {
            id: row.id,
            category: showCategoryPicker ? (category ? category.id : null) : null,
            category_type: categoryType,
            account_to: showAccountPicker ? (accountTo ? accountTo.id : null) : null,
            comment: showComment ? (comment ? comment : null) : null,
            tag_ids: showTags ? tags.map(tag => tag.id) : [],
        };
        if (categoryType === TransactionType.Exchange && row.positive_flow) {
            payload.transfer_amount = accountTo ? parseAmountInput(transferAmount) : null;
        } else if (modifiedTransferAmount || row.transfer_amount != null) {
            payload.transfer_amount = null;
        }
        patchBulkUploadRow(payload);
        setModifiedCategory(false);
        setModifiedAccountTo(false);
        setModifiedCategoryType(false);
        setModifiedComment(false);
        setModifiedTransferAmount(false);
        setModifiedTags(false);
    }

    const canSplit =
        !isChild
        && !isUniteParent
        && !isUniteChild
        && status === 'UPLOADED'
        && categoryType === TransactionType.Expense
        && !row.parent_row;

    const isExpenseType = categoryType === TransactionType.Expense;
    const isIncomeType = categoryType === TransactionType.Income;
    const isTransferType = categoryType === TransactionType.Transfer;
    const amountColorClass = isTransferType
        ? (row.positive_flow ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800")
        : isExpenseType
            ? "bg-red-100 text-red-800"
            : isIncomeType
                ? "bg-green-100 text-green-800"
                : "bg-blue-100 text-blue-800";
    const childTotalValue = childDraft?.total ?? formatAbsAmount(row.total ?? row.amount);

    return (
        <tr className={
            status === 'EXCLUDED' && !isUniteChild ? "bg-red-100"
                : status === 'SPLIT' && isRowConfigured ? "bg-green-100"
                : status === 'SPLIT' || status === 'SPLITED' ? "bg-gray-100"
                : status === 'UNITE' && isRowConfigured && !modified ? "bg-green-100"
                : status === 'UNITE' ? "bg-indigo-50"
                : status === 'UNITED' ? "bg-green-100"
                : isUpdateStatus ? "bg-yellow-50"
                : isRowConfigured && !modified && !isChild ? "bg-green-100"
                : isChild && isRowConfigured ? "bg-green-50"
                : row.is_blocked ? "bg-amber-50"
                : ""
        }>
            <td className="px-2 py-1.5 whitespace-nowrap text-sm text-gray-500">
                <div className="flex h-5 items-center">
                    <input
                        id={"checkbox_" + row.id}
                        name="row_checkbox"
                        type="checkbox"
                        value={row.id}
                        checked={checked}
                        onChange={handleCategoryChange}
                        disabled={isSplitParent || isUniteParent || isUniteChild}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                </div>
            </td>
            <td className="px-1 py-1.5 whitespace-nowrap text-sm text-gray-500">
                {!isChild && !isUniteParent && (
                    <Menu as="div" className="relative inline-block text-left">
                        <Menu.Button className="inline-flex items-center justify-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800">
                            <EllipsisVerticalIcon className="h-5 w-5"/>
                        </Menu.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items className="absolute left-0 z-20 mt-1 w-36 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <Menu.Item disabled={!canSplit}>
                                    {({active, disabled}) => (
                                        <button
                                            type="button"
                                            disabled={disabled}
                                            onClick={() => onSplit?.()}
                                            className={classNames(
                                                active && !disabled ? 'bg-gray-100' : '',
                                                disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700',
                                                'block w-full px-4 py-2 text-left text-sm'
                                            )}
                                        >
                                            Split
                                        </button>
                                    )}
                                </Menu.Item>
                            </Menu.Items>
                        </Transition>
                    </Menu>
                )}
            </td>
            <td className={classNames(
                "px-2 py-1.5 whitespace-nowrap text-xs text-gray-500",
                isNestedChild ? "pl-6" : ""
            )}>
                {format(new Date(row.date), 'dd MMM yyyy HH:MM:SS')}
            </td>
            <td className="px-2 py-1.5 whitespace-nowrap">
                {isChild || (isUniteParent && status === 'UNITE') ? (
                    <span className="text-xs text-gray-400">—</span>
                ) : (
                    <span
                        className={classNames(
                            "px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full",
                            amountColorClass
                        )}>
                        {row.currency_symbol + ' ' + formatAbsAmount(row.amount)}
                    </span>
                )}
            </td>
            <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-500">
                {isChild || row.is_blocked ? '—' : row.commission_moneyback}
            </td>
            <td className="px-2 py-1.5 whitespace-nowrap">
                {isChild ? (
                    <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">{accountCurrencySymbol}</span>
                        <input
                            type="text"
                            value={childTotalValue}
                            onChange={(e) => onChildDraftChange?.({total: e.target.value})}
                            className="w-20 rounded-md border border-gray-300 px-1.5 py-1 text-xs text-gray-900 shadow-sm
                                       focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                    </div>
                ) : (
                    <span
                        className="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-700">
                        {accountCurrencySymbol + ' ' + formatAbsAmount(row.total)}
                    </span>
                )}
            </td>
            <td className="px-2 py-1.5 text-sm text-gray-500 max-w-xs">
                <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1.5">
                        {row.is_blocked && (
                            <span className="px-1.5 inline-flex w-fit text-xs leading-4 font-semibold rounded-full bg-amber-100 text-amber-800">
                                Blocked
                            </span>
                        )}
                        {isChild && (
                            <span className="px-1.5 inline-flex w-fit text-xs leading-4 font-semibold rounded-full bg-gray-200 text-gray-700">
                                Split
                            </span>
                        )}
                        {isUniteChild && (
                            <span className="px-1.5 inline-flex w-fit text-xs leading-4 font-semibold rounded-full bg-indigo-100 text-indigo-800">
                                United
                            </span>
                        )}
                        <span className="font-bold text-sm text-gray-700">
                            {row.category_description || '—'}
                        </span>
                    </div>
                    <span className="text-xs text-gray-500 leading-snug">{row.description}</span>
                </div>
            </td>
            <td className="px-2 py-1.5 whitespace-nowrap text-sm text-gray-500">
                <select
                    value={categoryType}
                    onChange={handleCategoryTypeChange}
                    disabled={isTypeSelectLocked}
                    className="block min-w-[6.5rem] rounded-md border border-gray-300 bg-white px-1.5 py-1 shadow-sm
                               focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs text-gray-900"
                >
                    {availableCategoryTypeOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
                {modifiedCategoryType && <div className="static w-full">
                    <div className="relative">
                        <div className="absolute top-px rounded-md bg-green-300 left-0 right-0" style={{height: "3px"}}></div>
                    </div>
                </div>}
            </td>
            <td className="px-2 py-1.5 whitespace-nowrap text-sm text-gray-500">
                {showCategoryPicker && <CategoryPicker label={null} category={category} setCategory={handleSetCategory}
                                                       categories={categoriesForType} smallerSize/>}
                {showAccountPicker && (
                    <AccountPicker
                        label={null}
                        account={accountTo}
                        setAccount={handleSetAccountTo}
                        accounts={accountsTo}
                        smallerSize
                    />
                )}
                {showLockedCategoryOrAccount && (
                    <span className="text-xs text-gray-600">
                        {(categoryType === TransactionType.Expense || categoryType === TransactionType.Income)
                            ? (category?.name || '—')
                            : (accountTo?.name || '—')}
                    </span>
                )}
                {(modifiedCategory || modifiedAccountTo) && <div className="static w-full">
                    <div className="relative">
                        <div className="absolute top-px rounded-md bg-green-300 left-0 right-0" style={{height: "3px"}}></div>
                    </div>
                </div>}
            </td>
            <td className="relative px-2 py-1.5 whitespace-nowrap text-sm text-gray-500">
                {showComment && (
                    <CommentField comment={comment} setComment={handleSetComment} withLabel={false} className="relative" smallerSize/>
                )}
                {showExchangeAmountField && (accountTo || sessionAccount) && (
                    <AmountField
                        label={null}
                        withLabel={false}
                        currency={accountTo?.currency || sessionAccount?.currency}
                        amount={transferAmount}
                        setAmount={handleSetTransferAmount}
                        smallerSize
                        className="relative"
                    />
                )}
                {showUpdateTotalAmountField && sessionAccount && (
                    <AmountField
                        label={null}
                        withLabel={false}
                        currency={sessionAccount.currency}
                        amount={totalAmount}
                        setAmount={handleSetTotalAmount}
                        smallerSize
                        className="relative"
                    />
                )}
                {!showComment && !showExchangeAmountField && !showUpdateTotalAmountField && (
                    isUniteChild || status === 'UNITED'
                        ? <span className="text-xs text-gray-600">{row.comment || '—'}</span>
                        : <span className="text-gray-400">—</span>
                )}
                {(modifiedComment || modifiedTransferAmount || modifiedTotalAmount) && <div className="static w-full">
                    <div className="relative">
                        <div className="absolute top-px rounded-md bg-green-300 left-0 right-0" style={{height: "3px"}}></div>
                    </div>
                </div>}
            </td>
            <td className="relative px-2 py-1.5 whitespace-nowrap text-sm text-gray-500">
                {showTags && tagsList && tagsList.length !== 0 && (
                    <TagsPicker label={null} tags={tags} setTags={handleSetTags} tagsList={tagsList} smallerSize/>
                )}
                {!showTags && (
                    isUniteChild || status === 'UNITED'
                        ? <span className="text-xs text-gray-600">
                            {(row.tags || []).length > 0 ? (row.tags || []).map(tag => tag.name).join(', ') : '—'}
                          </span>
                        : <span className="text-gray-400">—</span>
                )}
                {modifiedTags && <div className="static w-full">
                    <div className="relative">
                        <div className="absolute top-px rounded-md bg-green-300 left-0 right-0" style={{height: "3px"}}></div>
                    </div>
                </div>}
            </td>
            <td className="px-2 py-1.5 whitespace-nowrap text-sm text-gray-500">
                <div className={classNames(
                    status === 'UPLOADED'
                        ? "bg-blue-100 text-blue-800"
                        : status === 'DUPLICATE'
                            ? "bg-gray-100 text-gray-700"
                            : status === 'EXCLUDED'
                                ? "bg-red-100 text-red-800"
                                : status === 'SPLIT'
                                    ? "bg-indigo-100 text-indigo-800"
                                : status === 'SPLITED'
                                    ? "bg-gray-200 text-gray-600"
                                    : status === 'UNITE'
                                        ? "bg-indigo-100 text-indigo-800"
                                        : status === 'UNITED'
                                            ? "bg-green-100 text-green-800"
                                    : status === 'UPDATE' || status === 'UPDATED'
                                        ? "bg-yellow-100 text-yellow-800"
                                        : "bg-green-100 text-green-800",
                    "px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full")}
                >
                    {status === 'UPLOADED' ? 'Uploaded'
                        : status === 'DUPLICATE' ? 'Duplicate'
                            : status === 'EXCLUDED' ? 'Excluded'
                                : status === 'SPLIT' ? 'Split'
                                : status === 'SPLITED' ? 'Splited'
                                    : status === 'UNITE' ? 'Unite'
                                        : status === 'UNITED' ? 'United'
                                    : status === 'UPDATE' ? 'Update'
                                        : status === 'UPDATED' ? 'Updated'
                                            : 'Created'}
                </div>
            </td>
            <td className="px-2 py-1.5 whitespace-nowrap text-right text-sm font-medium">
                {isChild ? (
                    <button
                        className={classNames(
                            "cursor-pointer text-xs",
                            canDeleteChild ? "text-red-600 hover:text-red-900" : "text-gray-300 cursor-not-allowed"
                        )}
                        disabled={!canDeleteChild}
                        onClick={onDeleteChild}
                    >
                        Delete
                    </button>
                ) : isUniteChild ? (
                    <span className="text-xs text-gray-400">—</span>
                ) : (
                    <div className="flex flex-col items-end gap-0.5">
                        <button className="cursor-pointer text-green-600 hover:text-green-900 text-xs"
                                onClick={handleSave}
                                disabled={
                                    (isSplitParent && status !== 'SPLIT')
                                    || (!isSplitParent && (status === 'UPDATED' || status === 'CREATED' || status === 'DUPLICATE' || status === 'SPLITED' || status === 'UNITED'))
                                }
                        >
                            Save
                        </button>
                        <button
                            className={classNames(
                                "cursor-pointer text-xs",
                                isUniteParent
                                    ? (status === 'UNITE' ? "text-red-600 hover:text-red-900" : "text-gray-400")
                                    : isSplitParent
                                    ? (status === 'SPLIT'
                                        ? (splitExcludeLabel === 'Exclude' ? "text-red-600 hover:text-red-900" : "text-blue-600 hover:text-blue-900")
                                        : "text-gray-400")
                                    : status === 'UPLOADED' ? "text-red-600 hover:text-red-900"
                                        : status === 'EXCLUDED' ? "text-blue-600 hover:text-blue-900"
                                            : "text-gray-400"
                            )}
                            disabled={
                                isPatching
                                || (isUniteParent && status !== 'UNITE')
                                || (isSplitParent && status !== 'SPLIT')
                                || (!isSplitParent && !isUniteParent && (isLockedStatus || status === 'UPDATE'))
                            }
                            onClick={handleToggleStatus}
                        >
                            {isUniteParent
                                ? 'Cancel'
                                : isSplitParent
                                ? (splitExcludeLabel || 'Exclude')
                                : status === 'UPLOADED' ? 'Exclude'
                                    : status === 'EXCLUDED' ? 'Restore'
                                        : '—'}
                        </button>
                    </div>
                )}
            </td>
        </tr>
    );
};

export default BulkUploadRowItem;
